import { useEffect, useRef } from 'react';
import { setOpenItem } from '../lib/openItemStore';
import { isItemEditorRoute } from '../components/shell/surfaces';

/**
 * Keeps the open-item store reflecting what is CURRENTLY open, so the agent's
 * active target (the "Acting on" chip + the per-turn context overlay) is never
 * the *last* item the user happened to open.
 *
 * Two clears, both mounted once in the signed-in shell:
 *
 *  1. **Off an item editor** — when the route is not a Document / Work Item /
 *     Project editor (a list, the dashboard, the agent panel, settings…), there
 *     is nothing open, so the store is cleared. Item-editor routes are left
 *     alone: their page's `useTrackOpenItem` is the authority for the open item
 *     (and runs as a child effect, before this parent effect, so it is never
 *     clobbered when navigating INTO an item).
 *
 *  2. **On org switch** — an item opened in the previous org must not linger as
 *     the active target after switching (this also covers the cross-tab switch
 *     that adopts a new org WITHOUT navigating, where clear #1 would not fire
 *     because the route is unchanged). The first org resolution is skipped so a
 *     deep link straight to an item editor is not clobbered on load.
 */
export function useClearStaleOpenItem(
  pathname: string,
  activeOrgId: string | null
): void {
  useEffect(() => {
    if (!isItemEditorRoute(pathname)) setOpenItem(null);
  }, [pathname]);

  const orgClearArmed = useRef(false);
  useEffect(() => {
    if (activeOrgId === null) return;
    if (!orgClearArmed.current) {
      orgClearArmed.current = true;
      return;
    }
    setOpenItem(null);
  }, [activeOrgId]);
}
